/**
 * What the Live-kaart shows of Flanders: a middle (Belgian Lambert 72, in metres, the coordinates Tafo
 * keeps for streets, the shop and the couriers) and a level of the GRB tiles (server: live-map/grid.ts).
 * No map library: a point is placed by plain arithmetic, a tile is a picture of 256 pixels.
 */

/** The grid "BPL72VL" of Digitaal Vlaanderen. */
export const GRID = { originX: 9_928, originY: 329_072, tilePixels: 256 } as const;

export interface Point {
  x: number;
  y: number;
}

export interface View {
  /** The middle of the map, in Lambert 72. */
  x: number;
  y: number;
  /** A level of the tiles; between two levels while zooming (the tiles of the nearest one, scaled). */
  level: number;
}

export interface Size {
  width: number;
  height: number;
}

export function metresPerPixel(level: number): number {
  return 1024 / 2 ** level;
}

/** Where a point is drawn, in pixels from the top-left of the map. North is up: y grows downwards. */
export function toScreen(view: View, size: Size, point: Point): { left: number; top: number } {
  const metres = metresPerPixel(view.level);
  return {
    left: (point.x - view.x) / metres + size.width / 2,
    top: (view.y - point.y) / metres + size.height / 2,
  };
}

/** The view moved by a drag of `dx`, `dy` pixels: the map follows the hand. */
export function panned(view: View, dx: number, dy: number): View {
  const metres = metresPerPixel(view.level);
  return { ...view, x: view.x - dx * metres, y: view.y + dy * metres };
}

/**
 * The view at another level, with the point under `focus` (pixels from the top-left of the map) staying
 * where it is: zooming towards the hand, the mouse or the middle of two fingers.
 */
export function zoomedAbout(view: View, size: Size, focus: { left: number; top: number }, level: number): View {
  const before = metresPerPixel(view.level);
  const after = metresPerPixel(level);
  const dx = focus.left - size.width / 2;
  const dy = focus.top - size.height / 2;
  // the place under the focus: the same before and after
  const x = view.x + dx * before;
  const y = view.y - dy * before;
  return { x: x - dx * after, y: y + dy * after, level };
}

/** The level whose tiles are drawn for a view: the nearest one there is. */
export function tileLevelOf(level: number, minLevel: number, maxLevel: number): number {
  return Math.min(maxLevel, Math.max(minLevel, Math.round(level)));
}

export interface PlacedTile {
  level: number;
  col: number;
  row: number;
  left: number;
  top: number;
  /** In pixels: 256 at its own level, larger or smaller while the view is between two levels. */
  size: number;
}

/**
 * The tiles of one level that cover the map, each with where and how large it is drawn. By default the
 * level of the view; another level (the one before a zoom) is drawn to the scale of the view.
 */
export function visibleTiles(view: View, size: Size, tileLevel = view.level): PlacedTile[] {
  const metres = metresPerPixel(view.level);
  const tileMetres = metresPerPixel(tileLevel) * GRID.tilePixels;
  const tilePixels = tileMetres / metres;
  const minX = view.x - (size.width / 2) * metres;
  const maxX = view.x + (size.width / 2) * metres;
  const maxY = view.y + (size.height / 2) * metres;
  const minY = view.y - (size.height / 2) * metres;
  const firstCol = Math.floor((minX - GRID.originX) / tileMetres);
  const lastCol = Math.floor((maxX - GRID.originX) / tileMetres);
  const firstRow = Math.floor((GRID.originY - maxY) / tileMetres);
  const lastRow = Math.floor((GRID.originY - minY) / tileMetres);
  const tiles: PlacedTile[] = [];
  for (let row = firstRow; row <= lastRow; row++) {
    for (let col = firstCol; col <= lastCol; col++) {
      const corner = toScreen(view, size, {
        x: GRID.originX + col * tileMetres,
        y: GRID.originY - row * tileMetres,
      });
      tiles.push({ level: tileLevel, col, row, left: corner.left, top: corner.top, size: tilePixels });
    }
  }
  return tiles;
}

/** Room around the points, in pixels: a marker and its name stay inside the map. */
const MARGIN = 56;
/** Only the shop, or one point: close enough to read the streets around it. */
const ONE_POINT_LEVEL = 9;

/**
 * The view that shows all the points as large as possible: the sharpest level where they all fit, with
 * room around them.
 */
export function fitView(points: readonly Point[], size: Size, minLevel: number, maxLevel: number): View | null {
  if (points.length === 0) return null;
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const middle = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
  const room = { width: Math.max(1, size.width - 2 * MARGIN), height: Math.max(1, size.height - 2 * MARGIN) };
  if (maxX - minX < 1 && maxY - minY < 1) {
    return { ...middle, level: Math.min(maxLevel, Math.max(minLevel, ONE_POINT_LEVEL)) };
  }
  for (let level = maxLevel; level > minLevel; level--) {
    const metres = metresPerPixel(level);
    if ((maxX - minX) / metres <= room.width && (maxY - minY) / metres <= room.height) {
      return { ...middle, level };
    }
  }
  return { ...middle, level: minLevel };
}

/** One colour per courier, the same on the map and in the list; Tafo's red stays the shop's. */
export const COURIER_COLOURS = ["#2563eb", "#059669", "#7c3aed", "#d97706", "#0891b2", "#db2777", "#4d7c0f"];

export function courierColour(index: number): string {
  return COURIER_COLOURS[index % COURIER_COLOURS.length]!;
}

/** The address of one tile, from the template the server gives ({level}, {col}, {row}). */
export function tileUrl(template: string, tile: Pick<PlacedTile, "level" | "col" | "row">): string {
  return template
    .replace("{level}", String(tile.level))
    .replace("{col}", String(tile.col))
    .replace("{row}", String(tile.row));
}
